import { useMemo } from 'react';
import { getName } from '@home/utils/common';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import type { IFormatRule } from '@home/utils/dbDataFormat/_type';
import type {
  FormatRule,
  IUseEventMetricTrendTableParams,
  RadioConfig,
  RadioOption,
  SeriesConfig,
  SeriesDataItem,
  TEventMergeColumnCfg,
  TTrendTableColumnOption,
  TTrendTableDownloadPack,
  TTrendTableModel,
  TTrendTableSeriesMetaColumn,
} from './types';
import { formatEventDataSource } from './utils';

export type {
  TTrendTableColumnOption,
  TTrendTableDownloadPack,
  TTrendTableModel,
  IUseEventMetricTrendTableParams,
} from './types';

/** 异动散点列 metricKey 前缀，表格中不展示 */
const ANOMALY_METRIC_KEY_PREFIX = '_anomaly_';

/** 与折线 series 一致的指标列解析，用于趋势表格（CommonBaseTable 列映射） */
function getTrendTableSeriesMeta(
  seriesConfig: SeriesConfig[],
  radioConfig: RadioConfig | undefined,
  effectiveRadioValue: string,
  isEN: boolean,
): TTrendTableSeriesMetaColumn[] {
  return (seriesConfig || []).map((config, index) => {
    const currentRadioOption = radioConfig?.options?.find(
      (opt: RadioOption) => opt.value === effectiveRadioValue,
    );
    let finalMetricKey = config.metricKey;
    let finalDateKey = config.dateKey;
    let finalCnName = config.cnName;
    let finalEnName = config.enName;

    if (radioConfig && currentRadioOption) {
      const { replacements } = currentRadioOption;
      if (Array.isArray(replacements)) {
        const targetIndexes = radioConfig.targetSeriesIndexes || [];
        const replaceIdx = targetIndexes.indexOf(index);
        if (replaceIdx !== -1 && replacements[replaceIdx]) {
          const r = replacements[replaceIdx];
          finalMetricKey = r.metricKey || finalMetricKey;
          finalDateKey = r.dateKey || finalDateKey;
          finalCnName = r.cnName || finalCnName;
          finalEnName = r.enName || finalEnName;
        }
      } else if (index === radioConfig.targetSeriesIndex) {
        finalMetricKey = currentRadioOption.metricKey;
        finalDateKey = currentRadioOption.dateKey;
        if (currentRadioOption.cnName) finalCnName = currentRadioOption.cnName;
        if (currentRadioOption.enName) finalEnName = currentRadioOption.enName;
      }
    }

    return {
      colKey: `trend_col_${index}`,
      header: getName(isEN, finalEnName, finalCnName),
      metricKey: finalMetricKey,
      dateKey: finalDateKey || 'date',
      formatRule: config.formatRule,
    };
  });
}

/** 指标列表头较长时给足 minWidth，避免表头被裁切 */
function metricColumnMinWidth(header: string): number {
  const len = String(header).length;
  return Math.min(128, Math.max(112, len * 8 + 24));
}

/**
 * KPI 趋势图切表格视图：列定义（key/header，供 CommonBaseTable 映射）、行数据、downloadPack。
 * 日期列展示固定为 YYYY-MM-DD（与 chartConfig.dateFormatRule 无关）。
 */
export function useEventMetricTrendTable(
  params: IUseEventMetricTrendTableParams,
): TTrendTableModel | null {
  const {
    effectiveShowTableView,
    seriesConfig,
    seriesData,
    radioConfig,
    effectiveRadioValue,
    isEN,
    formatMessage,
    rawEventList,
    downloadColumnsResolved,
  } = params;

  return useMemo(() => {
    if (!effectiveShowTableView || !seriesConfig?.length) {
      return null;
    }

    const colMetas = getTrendTableSeriesMeta(
      seriesConfig,
      radioConfig,
      effectiveRadioValue,
      isEN,
    ).filter((col) => !String(col.metricKey).startsWith(ANOMALY_METRIC_KEY_PREFIX));

    const dateKeyForRow = colMetas[0]?.dateKey || 'date';
    const dateColKey = 'date';
    /** 表格日期列与折线轴 dateFormatRule 解耦，统一展示 YYYY-MM-DD */
    const tableDateDisplayRule = 'YYYY-MM-DD';

    const resolvedCols = downloadColumnsResolved || [];
    const eventColMetas = resolvedCols.filter(
      (c) => String(c?.type) === 'event' && String(c?.code || '').length > 0,
    ) as Array<Record<string, unknown>>;

    const hasEventMerge =
      Array.isArray(rawEventList) &&
      rawEventList.length > 0 &&
      eventColMetas.length > 0 &&
      resolvedCols.length > 0;

    const rows = (
      hasEventMerge
        ? formatEventDataSource(
          (seriesData || []) as { date: string; }[],
          rawEventList,
          {
            columns: resolvedCols as TEventMergeColumnCfg[],
          },
          isEN,
        )
        : seriesData || []
    ) as SeriesDataItem[];

    /** 页面表格仅展示日期 + 指标；事件列仅写入 downloadPack 供导出 */
    const tableOption: TTrendTableColumnOption[] = [
      {
        key: dateColKey,
        header: formatMessage({ id: 'overview_table_date' }),
        align: 'left',
        width: 128,
        minWidth: 128,
      },
      ...colMetas.map((c) => ({
        key: c.colKey,
        header: c.header,
        align: 'right' as const,
        // minWidth: metricColumnMinWidth(c.header),
      })),
    ];

    const downloadOption: TTrendTableColumnOption[] = [
      ...tableOption,
      ...eventColMetas.map((ec) => {
        const code = String(ec.code ?? '');
        const header = String(ec.name ?? code);
        return {
          key: code,
          header,
          align: 'left' as const,
          minWidth: Math.max(160, metricColumnMinWidth(header)),
          /** pageConfig.wrapText；未配置时事件列默认换行，避免导出格展示不全 */
          wrapText:
            ec.wrapText === undefined ? true : Boolean(ec.wrapText),
        };
      }),
    ];

    const tableList = rows.map((row) => {
      const dateRaw =
        (row as SeriesDataItem)[dateKeyForRow] ?? (row as SeriesDataItem).date;
      const rec: Record<string, string> = {
        [dateColKey]: String(
          dateTimeFormat(String(dateRaw ?? ''), tableDateDisplayRule) ||
            dateRaw ||
            '',
        ),
      };
      colMetas.forEach((col) => {
        const val = (row as SeriesDataItem)[col.metricKey];
        rec[col.colKey] = String(
          dataFormat({
            value: val as number,
            // EventMetricTrend.FormatRule.type 为可选，IFormatRule.type 必填；
            // 运行时由配置侧保证字段完整，此处做结构断言对齐 dataFormat 入参
            formatRule: col.formatRule as unknown as IFormatRule,
          }) || '-',
        );
      });
      return rec;
    });

    const downloadData = rows.map((row) => {
      const dateRaw =
        (row as SeriesDataItem)[dateKeyForRow] ?? (row as SeriesDataItem).date;
      const rec: Record<string, unknown> = {
        [dateColKey]:
          dateTimeFormat(String(dateRaw ?? ''), 'YYYY-MM-DD') ||
          String(dateRaw ?? ''),
      };
      colMetas.forEach((col) => {
        rec[col.colKey] = (row as SeriesDataItem)[col.metricKey];
      });
      eventColMetas.forEach((ec) => {
        const code = String(ec.code ?? '');
        rec[code] = (row as Record<string, unknown>)[code] ?? '';
      });
      return rec;
    });

    return {
      tableOption,
      tableList,
      downloadPack: {
        option: downloadOption,
        data: tableList,
        downloadData,
      },
    };
  }, [
    effectiveShowTableView,
    seriesConfig,
    seriesData,
    radioConfig,
    effectiveRadioValue,
    isEN,
    formatMessage,
    rawEventList,
    downloadColumnsResolved,
  ]);
}
